import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const ManageModal = ({ business, onSave, onClose }) => {
  const [staffing, setStaffing] = useState(business?.staffingLevel || 75);
  const [inventory, setInventory] = useState(business?.inventoryLevel || 60);
  const [security, setSecurity] = useState(business?.securityLevel || 50);

  const adjustedRevenue = Math.round(business?.revenue * (staffing / 100) * 1.1);
  const adjustedCost = Math.round(business?.operatingCost * (0.5 + staffing / 200) + (inventory / 100) * 5000 + (security / 100) * 3000);
  const adjustedNet = adjustedRevenue - adjustedCost;

  const SliderControl = ({ label, value, onChange, icon, color, description }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name={icon} size={13} color={color} />
          <span className="font-caption text-xs text-foreground">{label}</span>
        </div>
        <span className="font-heading text-xs font-semibold" style={{ color }}>{value}%</span>
      </div>
      <input
        type="range" min={0} max={100} value={value}
        onChange={e => onChange(Number(e?.target?.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
        style={{ accentColor: color }}
      />
      <p className="font-caption text-xs text-muted-foreground">{description}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.85)" }}>
      <div className="w-full max-w-lg rounded-2xl border overflow-hidden" style={{ background: "var(--color-card)", borderColor: "var(--color-border)" }}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--color-border)" }}>
          <div>
            <h3 className="font-heading text-sm font-bold text-foreground">Manage: {business?.name}</h3>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">Adjust operational parameters</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-96 overflow-y-auto">
          <SliderControl
            label="Staffing Level" value={staffing} onChange={setStaffing}
            icon="Users" color="var(--color-primary)"
            description="Higher staffing increases revenue but raises operating costs"
          />
          <SliderControl
            label="Inventory Level" value={inventory} onChange={setInventory}
            icon="Package" color="var(--color-accent)"
            description="More inventory reduces stockouts but increases holding costs"
          />
          <SliderControl
            label="Security Level" value={security} onChange={setSecurity}
            icon="Shield" color="var(--color-success)"
            description="Higher security reduces crime risk and evidence accumulation"
          />

          {/* Projected Impact */}
          <div className="rounded-xl p-4 border" style={{ background: "rgba(0,0,0,0.3)", borderColor: "var(--color-border)" }}>
            <div className="font-caption text-xs text-muted-foreground mb-3 uppercase tracking-wider">Projected Impact</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Revenue", value: `$${adjustedRevenue?.toLocaleString()}`, color: "var(--color-success)" },
                { label: "Costs", value: `$${adjustedCost?.toLocaleString()}`, color: "var(--color-error)" },
                { label: "Net", value: `$${adjustedNet?.toLocaleString()}`, color: adjustedNet >= 0 ? "var(--color-success)" : "var(--color-error)" },
              ]?.map(item => (
                <div key={item?.label} className="text-center">
                  <div className="font-caption text-xs text-muted-foreground mb-1">{item?.label}</div>
                  <div className="font-heading text-sm font-bold" style={{ color: item?.color }}>{item?.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t" style={{ borderColor: "var(--color-border)" }}>
          <Button variant="outline" fullWidth onClick={onClose}>Cancel</Button>
          <Button variant="default" fullWidth iconName="Save" iconPosition="left" onClick={() => { onSave({ ...business, staffingLevel: staffing, inventoryLevel: inventory, securityLevel: security }); onClose(); }}>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManageModal;